import { useContext, useCallback, useEffect, useState, useRef } from "react";
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import EmailCodeVerification from "../EmailCodeVerification";
import { useNavigate } from "react-router-dom";
import GoogleMapView from "../GoogleMapView";
import GoogleAddressSearch from "../GoogleAddressSearch";
import { GoogleMapsContext } from "../../context/GoogleMapsProvider";
import Cgu from "../Cgu";

export default function InscriptionDonneurOrdre({ onClose, onSwitch }) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const logoInputRef = useRef(null);
    const [step, setStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
    const [errors, setErrors] = useState({});
    const [loadingSiret, setLoadingSiret] = useState(false);
    const [errorSiret, setErrorSiret] = useState("");
    const [secteurs, setSecteurs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inscriptionSuccess, setInscriptionSuccess] = useState(false);
    const [cguAccepted, setCguAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [formData, setFormData] = useState({
        // Étape 1 : Login
        email: "",
        password: "",
        passwordRepeat: "",
        token: "",
        // Étape 2 : Infos personnelles
        nom: "",
        prenom: "",
        telephone: "",
        fonction: "",
        billingAddress: "",
        billingAddress2: "",
        billingPostalCode: "",
        billingCity: "",
        billingCountry: "fr",
        billingLat: null,
        billingLng: null,
        secteur: "",
        logo: "",
        // Étape 3 : Infos entreprise
        raisonSociale: "",
        siret: "",
        numTva: "",
    });
    const isValidSiret = (siret) => {
        if (!/^\d{14}$/.test(siret)) return false;
        let sum = 0;
        for (let i = 0; i < 14; i++) {
            let num = parseInt(siret[i]);
            if (i % 2 === 0) num *= 2;
            if (num > 9) num -= 9;
            sum += num;
        }
        return sum % 10 === 0;
    };
    const navigate = useNavigate();
    function computeFrenchTVAFromSiret(siret) {
        if (!siret || siret.length < 9) return "";

        const siren = siret.substring(0, 9);
        if (!/^\d{9}$/.test(siren)) return "";

        const key = (12 + 3 * (parseInt(siren, 10) % 97)) % 97;
        const formattedKey = key.toString().padStart(2, "0");

        return `FR${formattedKey}${siren}`;
    }
    const handleGetInfosPresta = async () => {
        setErrorSiret("");

        // Vérification SIRET
        if (!formData.siret || !isValidSiret(formData.siret)) {
            setErrorSiret("SIRET invalide");
            return;
        }
        try {
            setLoadingSiret(true);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/apiSirene?siret=${formData.siret}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Entreprise introuvable");
            }

            // Préremplissage du formulaire
            setFormData(prev => ({
                ...prev,
                raisonSociale: data.raisonSociale || prev.raisonSociale,
                typeCompany: data.statutPrestataire || prev.typeCompany,
                secteur: data.activitePrincipale || prev.secteur,
                billingAddress: data.adresse || prev.billingAddress,
                billingPostalCode: data.codePostal || prev.billingPostalCode,
                billingCity: data.ville || prev.billingCity,
                billingLat: data.latitude || prev.billingLat,
                billingLng: data.longitude || prev.billingLng,
                nom: data.lastNamePrestataire || prev.nom,
                prenom: data.firstNamePrestataire || prev.prenom,
                numTva: computeFrenchTVAFromSiret(data.siret || prev.siret)

            }));
            setStep(2);

        } catch (error) {
            setErrorSiret(error.message);
        } finally {
            setLoadingSiret(false);
        }


    }
    const refreshSecteurs = useCallback(() => {
        setLoading(true);
        fetch(`${apiUrl}/api/secteurs`)
            .then((res) => res.json())
            .then((data) => setSecteurs(data))
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    }, [apiUrl]);

    useEffect(() => {
        refreshSecteurs();
    }, [refreshSecteurs]);


    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFormData(prev => ({ ...prev, logo: file }));
        setLogoPreview(URL.createObjectURL(file));

        // Supprimer l'erreur files si maintenant valide
        setErrors(prev => {
            const { files, ...rest } = prev;
            return rest;
        });
    };

    const handleRemoveFile = () => {

        if (logoPreview) URL.revokeObjectURL(logoPreview);
        setLogoPreview(null);
        if (logoInputRef.current) logoInputRef.current.value = "";
        setFormData(prev => {
            const newData = { ...prev, logo: null };

            return newData;
        });

    };

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    const { isLoaded } = useContext(GoogleMapsContext);

    const validateField = (name, value) => {
        let newErrors = { ...errors };

        switch (name) {
            case "email":
                if (!value) {
                    newErrors.email = "L'email est requis";
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    newErrors.email = "L'adresse email est invalide.";
                } else {
                    // Si format valide, on vérifie côté API
                    delete newErrors.email;

                    fetch(`${apiUrl}/api/register/checkMail?email=${encodeURIComponent(value)}`)
                        .then((res) => res.json())
                        .then((data) => {
                            if (data.exists) {
                                // Si ton API renvoie { exists: true }
                                setErrors((prev) => ({
                                    ...prev,
                                    email: "Cet email est déjà utilisé."
                                }));
                            } else {
                                setErrors((prev) => {
                                    const { email, ...rest } = prev;
                                    return rest; // supprime l'erreur email
                                });
                            }
                        })
                        .catch((err) => {
                            console.error("Erreur vérification email:", err);
                        });
                }
                break;
            case "password":
                if (!value) newErrors.password = "Le mot de passe est requis";
                else if (!passwordRegex.test(value)) newErrors.password = "8 caractères minimum, 1 majuscule et 1 chiffre";
                else delete newErrors.password;
                break;

            case "passwordRepeat":
                if (value !== formData.password) newErrors.passwordRepeat = "Les mots de passe ne correspondent pas";
                else delete newErrors.passwordRepeat;
                break;

            case "nom":
                if (!value || value.length < 2) newErrors.nom = "Nom requis (min 2 caractères)";
                else delete newErrors.nom;
                break;

            case "prenom":
                if (!value || value.length < 2) newErrors.prenom = "Prénom requis (min 2 caractères)";
                else delete newErrors.prenom;
                break;

            case "fonction":
                if (!value || value.length < 2) newErrors.prenom = "fonction dans l'entreprise requise (min 2 caractères)";
                else delete newErrors.prenom;
                break;

            case "telephone":
                if (!/^\d{10}$/.test(value)) newErrors.telephone = "Numéro invalide (10 chiffres)";
                else delete newErrors.telephone;
                break;

            case "billingAddress":
                if (!value || value.length < 5) newErrors.billingAddress = "Adresse trop courte";
                else delete newErrors.billingAddress;
                break;

            case "billingPostalCode":
                if (!/^\d{5}$/.test(value)) newErrors.billingPostalCode = "Code postal invalide";
                else delete newErrors.billingPostalCode;
                break;

            case "billingCity":
                if (!value || value.length < 2) newErrors.billingCity = "Ville trop courte";
                else delete newErrors.billingCity;
                break;

            case "secteur":
                if (!value) newErrors.secteur = "Secteur requis";
                else delete newErrors.secteur;
                break;

            case "raisonSociale":
                if (!value) newErrors.raisonSociale = "Raison sociale requise";
                else delete newErrors.raisonSociale;
                break;

            case "siret":
                if (!/^\d{14}$/.test(value)) newErrors.siret = "SIRET invalide (14 chiffres)";
                else delete newErrors.siret;
                break;

            default:
                break;
        }

        setErrors(newErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const handlePlaceSelect = (place) => {
        // Extraction des composants d'adresse
        const components = place.address_components || [];
        let streetNumber = "", route = "", postal = "", city = "";

        components.forEach((c) => {
            if (c.types.includes("street_number")) streetNumber = c.long_name;
            if (c.types.includes("route")) route = c.long_name;
            if (c.types.includes("postal_code")) postal = c.long_name;
            if (c.types.includes("locality")) city = c.long_name;
            if (!city && c.types.includes("administrative_area_level_2")) city = c.long_name;
        });

        const formattedAddress = [streetNumber, route].filter(Boolean).join(" ") + ", " + postal + " " + city;

        // Fonction pour mettre à jour le formData
        const updateFormData = (lat, lng) => {
            setFormData((prev) => ({
                ...prev,
                billingAddress: [streetNumber, route].filter(Boolean).join(" "),
                billingPostalCode: postal,
                billingCity: city,
                billingLat: lat,
                billingLng: lng,
            }));

            validateField("billingAddress", [streetNumber, route].filter(Boolean).join(" "));
            validateField("billingPostalCode", postal);
            validateField("billingCity", city);
        };

        // Vérifie si place.geometry existe
        if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            updateFormData(lat, lng);
        } else {
            // Sinon, utiliser Geocoder pour obtenir lat/lng à partir de l'adresse
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: formattedAddress }, (results, status) => {
                if (status === "OK" && results[0]) {
                    const lat = results[0].geometry.location.lat();
                    const lng = results[0].geometry.location.lng();
                    updateFormData(lat, lng);
                } else {
                    console.error("Impossible de récupérer les coordonnées :", status, place);
                    updateFormData(null, null); // vide lat/lng si échec
                }
            });
        }
    };

    const handleSubmit = async () => {
        //e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        if (Object.keys(errors).length > 0) {
            alert("Veuillez corriger les erreurs avant de soumettre.");
            return;
        }

        try {
            const form = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key]) form.append(key, formData[key]);
            });
            if (formData.logo) form.append("logo", formData.logo);

            const response = await fetch(`${apiUrl}/api/register/donneur-ordre`, {
                method: "POST",
                body: form,
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.message || "Erreur lors de l'inscription");
                return;
            }

            const data = await response.json();
            console.log("Inscription réussie :", data);
            onClose();
        } catch (err) {
            console.error(err);
            alert("Impossible de contacter le serveur.");
        }
        setIsSubmitting(false);

    };

    const isStep1Valid = formData.email && formData.password && formData.passwordRepeat && !errors.email && !errors.password && !errors.passwordRepeat;
    const isStep2Valid = formData.nom && formData.prenom && formData.telephone && formData.fonction && formData.billingAddress && formData.billingPostalCode && formData.billingCity && formData.secteur &&
        !errors.nom && !errors.prenom && !errors.telephone && !errors.fonction && !errors.billingAddress && !errors.billingPostalCode && !errors.billingCity && !errors.secteur;
    const isStep3Valid = formData.raisonSociale && formData.siret && !errors.raisonSociale && !errors.siret;

    return (
        <div className="inscriptionModalOverlay">
            <div className="inscriptionModal">
                <button className="inscriptionModalClose" onClick={onClose}>×</button>
                <h2>Inscription Donneur d'Ordre</h2>
                {
                    !inscriptionSuccess && (
                        <>
                            {step === 0 && (
                                <div>
                                    <h3>Bienvenue sur INTERESEAUX</h3>
                                    <p>

                                        <strong style={{ color: "red" }}>Vous êtes ici pour créer un compte donneur d’ordres</strong> : cet espace est réservé aux
                                        entreprises et organisations qui publient des appels d’offres de grande envergure et recherchent des
                                        prestataires qualifiés à haut niveau d’expertise.
                                    </p>

                                    <p style={{ marginTop: "15px" }}>
                                        <strong>Vous n’êtes PAS au bon endroit si :</strong>
                                    </p>

                                    <ul style={{ marginTop: "10px" }}>
                                        <li>
                                            ⚠️ <strong>Vous êtes client “classique”</strong> et souhaitez simplement publier des projets pour
                                            trouver des prestataires.
                                            Dans ce cas, rendez-vous dans l’espace client en cliquant
                                            <strong onClick={() => navigate("/")} style={{ cursor: "pointer" }}> ici</strong>.
                                        </li>

                                        <li style={{ marginTop: "10px" }}>
                                            ⚠️ <strong>Vous êtes prestataire</strong> et cherchez des missions ou des appels d’offres auxquels répondre.
                                            Accédez à l’espace prestataire en cliquant
                                            <strong onClick={() => navigate("/espace-prestataire")} style={{ cursor: "pointer" }}> ici</strong>.
                                        </li>
                                    </ul>

                                    <button className="btn-primary" onClick={() => setStep(1)} style={{ marginTop: "15px" }}>
                                        CONTINUER
                                    </button>
                                </div>
                            )}
                            {
                                !inscriptionSuccess && (
                                    <form onSubmit={handleSubmit}>
                                        {step === 1 && (
                                            <>
                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Email</label>
                                                    <input type="email" name="email" value={formData.email} onChange={handleChange} />
                                                    {errors.email && <p className="error-text">{errors.email}</p>}
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Mot de passe</label>
                                                    <div className="password-wrapper">
                                                        <input
                                                            type={showPassword ? "text" : "password"}
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                        />
                                                        <span className="togglePassword" onClick={() => setShowPassword(v => !v)}>
                                                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                                        </span>
                                                    </div>
                                                    {errors.password && <p className="error-text">{errors.password}</p>}
                                                </div>
                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Répéter le mot de passe</label>
                                                    <div className="password-wrapper">
                                                        <input
                                                            type={showPasswordRepeat ? "text" : "password"}
                                                            name="passwordRepeat"
                                                            value={formData.passwordRepeat}
                                                            onChange={handleChange}
                                                        />
                                                        <span className="togglePassword" onClick={() => setShowPassword(v => !v)}>
                                                            <FontAwesomeIcon icon={showPasswordRepeat ? faEyeSlash : faEye} />
                                                        </span>
                                                    </div>
                                                    {errors.passwordRepeat && <p className="error-text">{errors.passwordRepeat}</p>}
                                                </div>

                                                <p className="modal-switch">
                                                    Déjà inscrit ? <span onClick={onSwitch}>Connectez-vous</span>
                                                </p>


                                                <div className="inscriptionDonneurOrdreModal-actions">
                                                    <button type="button" disabled={!isStep1Valid} onClick={() => setStep(11)}>Suivant</button>
                                                </div>
                                            </>
                                        )}
                                        {step === 11 && (
                                            <EmailCodeVerification
                                                formData={formData}
                                                onVerified={(token) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        token: token
                                                    }));
                                                    setTimeout(() => console.log("State réel :", token), 500);
                                                    setStep(13);
                                                }}
                                            />
                                        )}

                                        {
                                            step === 13 && (
                                                <>

                                                    <h3>Votre numéro de SIRET</h3>
                                                    <div className="inscriptionPrestataireModal-input-form">

                                                        <input type="text" name="siret" value={formData.siret} onChange={handleChange} required placeholder="SIRET"

                                                        />
                                                        {errorSiret && <p style={{ color: "red" }}>{errorSiret}</p>}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        onClick={handleGetInfosPresta}
                                                        disabled={loadingSiret}
                                                    >
                                                        {loadingSiret ? "Recherche..." : "Récupérer les informations"}
                                                    </button>


                                                    <div className="inscriptionClientModal-actions">
                                                        <button type="button" onClick={() => setStep(1)}>Précédent</button>
                                                    </div>
                                                </>
                                            )
                                        }
                                        {step === 2 && (
                                            <>
                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Nom</label>
                                                    <input type="text" name="nom" value={formData.nom} onChange={handleChange} />
                                                    {errors.nom && <p className="error-text">{errors.nom}</p>}
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Prénom</label>
                                                    <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} />
                                                    {errors.prenom && <p className="error-text">{errors.prenom}</p>}
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Téléphone</label>
                                                    <input type="text" name="telephone" value={formData.telephone} onChange={handleChange} />
                                                    {errors.telephone && <p className="error-text">{errors.telephone}</p>}
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Fonction dans l'entreprise</label>
                                                    <input type="text" name="fonction" value={formData.fonction} onChange={handleChange} />
                                                    {errors.fonction && <p className="error-text">{errors.fonction}</p>}
                                                </div>




                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Secteur d'activité de l'entreprise</label>
                                                    <select
                                                        name="secteur"
                                                        value={formData.secteur}
                                                        onChange={handleChange}
                                                        required
                                                    >
                                                        <option value="">-- Sélectionnez un secteur d'activité --</option>
                                                        {
                                                            secteurs && secteurs.length > 0 && secteurs.map((secteur) => (
                                                                <option value={secteur.idSecteur}>{secteur.labelSecteur}</option>
                                                            ))
                                                        }
                                                    </select>
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-actions">
                                                    <button type="button" onClick={() => setStep(1)}>Précédent</button>
                                                    <button type="button" disabled={!isStep2Valid} onClick={() => setStep(3)}>Suivant</button>
                                                </div>
                                            </>
                                        )}

                                        {step === 3 && (
                                            <div className="inscriptionDonneurOrdreModal-input-form">
                                                <label>Adresse de facturation</label>
                                                {isLoaded && !formData.billingLat && !formData.billingLng && (
                                                    <GoogleAddressSearch
                                                        value={[formData.billingAddress, formData.billingPostalCode, formData.billingCity].filter(Boolean).join(", ")}
                                                        onChange={(inputValue) => {
                                                            // Si l'utilisateur vide le champ, on supprime les infos d'adresse
                                                            if (!inputValue) {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    billingAddress: "",
                                                                    billingPostalCode: "",
                                                                    billingCity: "",
                                                                    billingLat: null,
                                                                    billingLng: null
                                                                }));
                                                            }
                                                        }}
                                                        onSelect={handlePlaceSelect}
                                                    />
                                                )}
                                                {formData.billingLat && formData.billingLng && (
                                                    <>
                                                        <div>
                                                            <h3>Adresse</h3>
                                                            <p>{formData.billingAddress}</p>
                                                            <p>{formData.billingPostalCode} {formData.billingCity}</p>
                                                            <button className="btn-primary" onClick={(e) => {
                                                                e.preventDefault();
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    billingAddress: "",
                                                                    billingCity: "",
                                                                    billingPostalCode: "",
                                                                    billingLat: null,
                                                                    billingLng: null
                                                                }));
                                                            }}>MODIFIER</button>
                                                        </div>
                                                        <GoogleMapView origin={{ lat: formData.billingLat, lng: formData.billingLng }} />

                                                    </>
                                                )}
                                                <div className="inscriptionDonneurOrdreModal-actions">
                                                    <button type="button" onClick={() => setStep(2)}>Précédent</button>
                                                    <button type="button" disabled={!isStep2Valid} onClick={() => setStep(4)}>Suivant</button>
                                                </div>
                                            </div>
                                        )}
                                        {step === 4 && (
                                            <>
                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Raison sociale</label>
                                                    <input type="text" name="raisonSociale" value={formData.raisonSociale} onChange={handleChange} />
                                                    {errors.raisonSociale && <p className="error-text">{errors.raisonSociale}</p>}
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>SIRET</label>
                                                    <input type="text" name="siret" value={formData.siret} onChange={handleChange} />
                                                    {errors.siret && <p className="error-text">{errors.siret}</p>}
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-input-form">
                                                    <label>Numéro TVA</label>
                                                    <input type="text" name="numTva" value={formData.numTva} onChange={handleChange} />
                                                </div>

                                                <div className="inscriptionDonneurOrdreModal-actions">
                                                    <button type="button" onClick={() => setStep(3)}>Précédent</button>
                                                    <button type="button" disabled={!isStep3Valid} onClick={() => setStep(5)}>Suivant</button>
                                                </div>
                                            </>
                                        )}
                                        {
                                            step === 5 && (<>
                                                <div className="inscriptionPrestataireModal-input-form">
                                                    <label>Logo de l'entreprise</label>
                                                    <input ref={logoInputRef} type="file" accept="image/*" onChange={(e) => handleFileChange(e, "logo")} />
                                                    {logoPreview && (
                                                        <div style={{ width: "fit-content", margin: "auto" }}>
                                                            <img src={logoPreview} alt="Prévisualisation logo" style={{ width: "150px" }} />
                                                            <button type="button" onClick={() => handleRemoveFile("logo")} style={{ display: "block", margin: "auto", marginTop: "5px", background: "red", color: "white", border: "none", borderRadius: "5px", padding: "5px 10px", cursor: "pointer" }}>Supprimer</button>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="inscriptionDonneurOrdreModal-actions">
                                                    <button type="button" onClick={() => setStep(4)}>Précédent</button>
                                                    <button type="button" onClick={() => setStep(6)}>Suivant</button>
                                                </div>
                                            </>
                                            )
                                        }
                                        {
                                            step === 6 && (
                                                <div>
                                                    <p style={{ marginBottom: "10px" }}>
                                                        Veuillez lire attentivement les conditions générales d'utilisation
                                                    </p>

                                                    {/* Conteneur scrollable */}
                                                    <div
                                                        style={{
                                                            maxHeight: "300px",
                                                            overflowY: "auto",
                                                            padding: "15px",
                                                            border: "1px solid #ddd",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fafafa"
                                                        }}
                                                    >
                                                        <Cgu />
                                                    </div>

                                                    <div style={{ marginTop: "20px" }}>
                                                        <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={cguAccepted}
                                                                onChange={(e) => setCguAccepted(e.target.checked)}
                                                            />
                                                            <span>
                                                                J'ai lu et j'accepte les <strong>Conditions Générales d'Utilisation</strong>.
                                                            </span>
                                                        </label>
                                                    </div>

                                                    <div className="inscriptionClientModal-actions">
                                                        <button
                                                            type="button"
                                                            onClick={() => setStep(3)}
                                                        >
                                                            Précédent
                                                        </button>

                                                        <button
                                                            type="button"
                                                            disabled={!cguAccepted}
                                                            onClick={async () => {
                                                await handleSubmit();
                                            }}
                                                        >
                                                            Valider
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        }

                                    </form>
                                )
                            }
                        </>
                    )
                }

                {
                    inscriptionSuccess && (
                        <div className="success-container">
                            <h2>🎉 Inscription réussie</h2>
                            <p>
                                Bienvenue sur notre plateforme, votre compte Donneur d'ordres INTERESEAUX est créé avec succès.
                            </p>

                            <div className="inscriptionClientModal-actions">
                                <button type="button" onClick={() => onClose()}>FERMER</button>
                                <button type="button" onClick={() => onSwitch("connexion")}>ME CONNECTER</button>
                            </div>
                        </div>
                    )
                }

            </div>
        </div>
    );
}
